import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { parseServerDate } from '../utils/formatDate';

const DIM_LABELS = {
  strategy:'Strategy Clarity', decision:'Decision Architecture', process:'Process Maturity',
  accountability:'Accountability System', execution:'Execution Rhythm', financial:'Financial Health',
  hr_culture:'HR & Culture', sales_marketing:'Sales & Marketing', operations:'Operations & Delivery',
  technology:'Technology & Digital', risk:'Risk & Compliance', customer_exp:'Customer Experience'
};
const DIM_COLORS = {
  strategy:'#06b6d4', decision:'#7c3aed', process:'#f59e0b', accountability:'#f97316',
  execution:'#22c55e', financial:'#10b981', hr_culture:'#ec4899', sales_marketing:'#f43f5e',
  operations:'#0ea5e9', technology:'#8b5cf6', risk:'#dc2626', customer_exp:'#d946ef'
};
const ScoreColor = s => !s ? 'var(--text3)' : s<=2?'var(--red)':s<=3?'var(--orange)':s<=3.8?'var(--yellow)':s<=4.5?'var(--green)':'var(--cyan)';

export default function ImportAudit({ showToast }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pageData = location.state || {};
  const { authFetch, user } = useAuth();
  const [companies, setCompanies]   = useState([]);
  const [selCompany, setSelCompany] = useState(pageData?.companyId || '');
  const [imports, setImports]       = useState([]);
  const [selected, setSelected]     = useState(null); // currently shown report
  const [uploading, setUploading]   = useState(false);
  const [loading, setLoading]       = useState(false);
  const [dragOver, setDragOver]     = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    authFetch('/api/companies').then(r=>r.json()).then(d=>setCompanies(Array.isArray(d)?d:[]));
  }, [authFetch]);

  useEffect(() => { if (selCompany) loadImports(); }, [selCompany]);

  const loadImports = async () => {
    setLoading(true);
    const res = await authFetch(`/api/imports/${selCompany}`);
    const d = await res.json();
    setImports(Array.isArray(d)?d:[]);
    // Auto-select first if available
    if (Array.isArray(d) && d.length > 0 && !selected) viewImport(d[0].id, selCompany);
    setLoading(false);
  };

  const upload = async (file) => {
    if (!file || !selCompany) { showToast('Select a company first', 'error'); return; }
    const ext = '.'+file.name.split('.').pop().toLowerCase();
    if (!['.pdf','.docx','.txt','.xlsx','.csv'].includes(ext)) { showToast('Unsupported file type. Use PDF, Word, Excel, TXT or CSV.', 'error'); return; }
    if (file.size > 20*1024*1024) { showToast('File too large (max 20MB)', 'error'); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/imports/${selCompany}`, { method:'POST', headers:{ Authorization:`Bearer ${token}` }, body: formData });
      const data = await res.json();
      if (res.ok) {
        showToast(`"${file.name}" analysed`);
        loadImports();
        // Show the new import immediately
        setSelected({ ...data, extracted_data: data.findings, original_name: file.name });
      } else if (data.upgrade) { showToast(data.error, 'error'); navigate('/billing'); }
      else showToast(data.error || 'Upload failed', 'error');
    } catch { showToast('Upload failed', 'error'); }
    setUploading(false);
  };

  const viewImport = async (id, cid) => {
    const res = await authFetch(`/api/imports/${cid||selCompany}/${id}`);
    const d = await res.json();
    if (res.ok) setSelected(d);
  };

  const deleteImport = async (id) => {
    if (!window.confirm('Delete this import?')) return;
    const res = await authFetch(`/api/imports/${selCompany}/${id}`, { method:'DELETE' });
    if (res.ok) {
      showToast('Deleted');
      setImports(prev=>prev.filter(i=>i.id!==id));
      if (selected?.id===id) setSelected(null);
    }
  };

  const findings = selected?.extracted_data
    ? (typeof selected.extracted_data === 'string' ? JSON.parse(selected.extracted_data) : selected.extracted_data)
    : null;

  const dimEntries = findings?.dimension_findings
    ? Object.entries(findings.dimension_findings).filter(([,v])=>v?.key_finding||v?.score_estimate)
    : [];

  const avgScore = dimEntries.length > 0
    ? (dimEntries.filter(([,v])=>v?.score_estimate).reduce((a,[,v])=>a+(v.score_estimate||0),0) / (dimEntries.filter(([,v])=>v?.score_estimate).length||1))
    : null;

  const maturityLabel = avgScore
    ? avgScore<=2?'Chaotic':avgScore<=3?'Reactive':avgScore<=3.8?'Stable':avgScore<=4.5?'Structured':'High-Performing'
    : null;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Import Existing Audit</div>
        <div className="page-sub">Upload any past consultant report, board review, or internal audit — AI maps it to the 12 Org DNA dimensions and generates a full analysis</div>
      </div>

      {user?.plan === 'free' && (
        <div className="plan-banner" style={{ marginBottom:20 }}>
          <div><div style={{fontWeight:600,fontSize:14,marginBottom:2}}>Pro Feature</div><div style={{fontSize:12,color:'var(--text2)'}}>Audit import and AI analysis requires Pro plan</div></div>
          <button className="btn btn-primary btn-sm" onClick={()=>navigate('/billing')}>Upgrade to Pro →</button>
        </div>
      )}

      {/* Top bar: company selector + upload */}
      <div className="card" style={{ marginBottom:20, padding:'14px 18px' }}>
        <div style={{ display:'flex', gap:14, alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:200 }}>
            <label className="form-label">Company</label>
            <select className="form-select" value={selCompany} onChange={e=>{ setSelCompany(e.target.value); setSelected(null); }}>
              <option value="">Select company...</option>
              {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {selCompany && (
            <>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.xlsx,.csv" style={{display:'none'}} onChange={e=>upload(e.target.files[0])} />
              <button className="btn btn-primary" disabled={uploading||user?.plan==='free'} onClick={()=>fileRef.current?.click()}>
                {uploading ? '⏳ Analysing…' : '📥 Upload Audit Report'}
              </button>
            </>
          )}
        </div>
      </div>

      {!selCompany && (
        <div className="card empty-state">
          <div className="empty-icon">📋</div>
          <div className="empty-title">Select a company to get started</div>
          <div className="empty-sub">Upload any past McKinsey, KPMG, or internal audit report. AI extracts findings and maps them to all 12 Org DNA dimensions.</div>
        </div>
      )}

      {selCompany && (
        <div style={{ display:'flex', gap:16, alignItems:'flex-start' }}>

          {/* Left: Import list */}
          <div style={{ width:240, flexShrink:0 }}>
            {/* Drop zone */}
            <div
              className={`drop-zone ${dragOver?'drag-over':''}`}
              style={{ marginBottom:14, padding:'24px 16px', opacity:user?.plan==='free'?0.5:1, cursor:user?.plan==='free'?'not-allowed':'pointer' }}
              onClick={()=>user?.plan!=='free'&&fileRef.current?.click()}
              onDragOver={e=>{e.preventDefault();setDragOver(true);}}
              onDragLeave={()=>setDragOver(false)}
              onDrop={e=>{e.preventDefault();setDragOver(false);upload(e.dataTransfer.files[0]);}}>
              <div style={{fontSize:28,marginBottom:8}}>{uploading?'⏳':'📥'}</div>
              <div style={{fontSize:13,fontWeight:600,marginBottom:4,color:'var(--text)'}}>
                {uploading?'Analysing…':'Drop file here'}
              </div>
              <div style={{fontSize:11,color:'var(--text3)'}}>PDF · Word · Excel · TXT</div>
            </div>

            {/* Past imports */}
            {loading ? (
              <div style={{textAlign:'center',padding:20}}><div className="loader" style={{margin:'0 auto'}}></div></div>
            ) : imports.length === 0 ? (
              <div style={{fontSize:12,color:'var(--text3)',textAlign:'center',padding:16}}>No past imports yet</div>
            ) : (
              <div>
                <div style={{fontSize:10,color:'var(--text3)',fontFamily:'var(--ff-m)',textTransform:'uppercase',letterSpacing:1,marginBottom:8}}>Past imports</div>
                {imports.map(imp=>(
                  <div key={imp.id}
                    onClick={()=>viewImport(imp.id)}
                    style={{ padding:'10px 12px', borderRadius:8, marginBottom:6, cursor:'pointer',
                      background: selected?.id===imp.id?'var(--blue-soft)':'var(--bg3)',
                      border: `1px solid ${selected?.id===imp.id?'rgba(0,212,255,0.3)':'var(--border)'}`,
                      transition:'all 0.12s' }}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:6}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:600,color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{imp.original_name}</div>
                        <div style={{fontSize:10,color:'var(--text3)',marginTop:2}}>{parseServerDate(imp.created_at).toLocaleDateString('en-IN')}</div>
                      </div>
                      <button className="btn btn-danger btn-sm" style={{fontSize:10,padding:'2px 6px',flexShrink:0}}
                        onClick={e=>{e.stopPropagation();deleteImport(imp.id);}}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Full analysis report */}
          <div style={{ flex:1, minWidth:0 }}>
            {!selected && !loading && (
              <div className="card empty-state" style={{padding:'48px 24px'}}>
                <div className="empty-icon">📋</div>
                <div className="empty-title">Upload an audit report to see the analysis</div>
                <div className="empty-sub">Supported: McKinsey reports, KPMG audits, board presentations, internal reviews, HR surveys, strategy documents</div>
              </div>
            )}

            {selected && (
              <div className="fade-in">
                {/* No API key notice */}
                {findings?.no_api_key && (
                  <div style={{ padding:'14px 18px', background:'rgba(245,166,35,0.08)', border:'1px solid rgba(245,166,35,0.25)', borderRadius:'var(--r)', marginBottom:16 }}>
                    <div style={{ fontWeight:600, fontSize:13, color:'var(--blue)', marginBottom:4 }}>⚠️ AI Analysis Unavailable</div>
                    <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.6 }}>
                      Add <code style={{fontFamily:'var(--ff-m)',fontSize:11}}>ANTHROPIC_API_KEY=sk-ant-...</code> to <code style={{fontFamily:'var(--ff-m)',fontSize:11}}>server/.env</code> and restart to get dimension mapping and insights.
                    </div>
                    {findings.text_preview && (
                      <details style={{marginTop:10}}>
                        <summary style={{fontSize:12,color:'var(--text3)',cursor:'pointer'}}>View extracted text preview</summary>
                        <pre style={{marginTop:8,fontSize:11,color:'var(--text3)',fontFamily:'monospace',whiteSpace:'pre-wrap',lineHeight:1.6,maxHeight:200,overflowY:'auto'}}>{findings.text_preview}</pre>
                      </details>
                    )}
                  </div>
                )}

                {!findings && (
                  <div className="card" style={{ textAlign:'center', padding:48 }}>
                    <div style={{ fontSize:32, marginBottom:12 }}>⚠️</div>
                    <div style={{ fontWeight:600, marginBottom:8 }}>Analysis data not available</div>
                    <div style={{ fontSize:12, color:'var(--text2)' }}>The file was uploaded but analysis failed. Try re-uploading or check the server logs.</div>
                  </div>
                )}

                {findings && (
                  <>{/* Report header */}
                  <div className="card" style={{ marginBottom:16, background:'linear-gradient(135deg,rgba(0,212,255,0.05),rgba(124,58,237,0.05))', borderColor:'rgba(201,151,58,0.2)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
                    <div>
                      <div style={{ fontSize:11, color:'var(--text3)', fontFamily:'var(--ff-m)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>
                        AUDIT INTELLIGENCE REPORT
                      </div>
                      <div style={{ fontFamily:'var(--ff-d)', fontSize:20, fontWeight:800, marginBottom:4 }}>
                        {selected.original_name?.replace(/\.(pdf|docx|xlsx|txt|csv)$/i,'')}
                      </div>
                      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }}>
                        {findings.consultant_name && findings.consultant_name !== 'unknown' && (
                          <span className="tag" style={{fontSize:11}}>📊 {findings.consultant_name}</span>
                        )}
                        {findings.report_date && findings.report_date !== 'unknown' && (
                          <span className="tag" style={{fontSize:11}}>📅 {findings.report_date}</span>
                        )}
                        <span className="tag" style={{fontSize:11,color:'var(--green)',borderColor:'rgba(34,197,94,0.3)'}}>✓ AI Analysed</span>
                      </div>
                    </div>
                    {avgScore && (
                      <div style={{ textAlign:'center', padding:'14px 20px', background:'var(--bg3)', borderRadius:'var(--r)', border:'1px solid var(--border)' }}>
                        <div style={{ fontFamily:'var(--ff-m)', fontSize:36, fontWeight:500, color:ScoreColor(avgScore), lineHeight:1 }}>
                          {avgScore.toFixed(1)}
                        </div>
                        <div style={{ fontSize:10, color:'var(--text3)', marginTop:4, fontFamily:'var(--ff-m)' }}>EST. SCORE / 5</div>
                        <div style={{ fontSize:11, color:ScoreColor(avgScore), marginTop:3, fontWeight:600 }}>{maturityLabel}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Executive summary */}
                {findings.summary && (
                  <div className="card" style={{ marginBottom:16, borderColor:'rgba(201,151,58,0.15)', background:'rgba(201,151,58,0.02)' }}>
                    <div className="card-title">Executive Summary</div>
                    <div style={{ fontSize:13, color:'var(--text2)', lineHeight:1.75 }}>{findings.summary}</div>
                  </div>
                )}

                {/* Dimension findings — the core report */}
                {dimEntries.length > 0 && (
                  <div style={{ marginBottom:16 }}>
                    <div className="sec-header"><div className="sec-title">Dimension Analysis</div><div className="sec-line"></div><span className="tag">{dimEntries.length} dimensions mapped</span></div>
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {dimEntries
                        .sort((a,b) => (a[1].score_estimate||3.5) - (b[1].score_estimate||3.5))
                        .map(([key, val]) => {
                          const color = DIM_COLORS[key] || 'var(--accent)';
                          const score = val.score_estimate;
                          return (
                            <div key={key} className="card" style={{ padding:'14px 18px', borderLeft:`3px solid ${color}` }}>
                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16, flexWrap:'wrap' }}>
                                <div style={{ flex:1 }}>
                                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                                    <span style={{ fontFamily:'var(--ff-m)', fontSize:10, color:color, letterSpacing:1 }}>
                                      {key.toUpperCase().replace('_',' ')}
                                    </span>
                                    <span style={{ fontWeight:600, fontSize:14, color:'var(--text)' }}>{DIM_LABELS[key]||key}</span>
                                  </div>
                                  {val.key_finding && (
                                    <div style={{ fontSize:13, color:'var(--text)', marginBottom:8, lineHeight:1.6 }}>
                                      <strong>📌 Finding:</strong> {val.key_finding}
                                    </div>
                                  )}
                                  {val.recommendation && (
                                    <div style={{ fontSize:12, color:'var(--text2)', padding:'8px 12px', background:'rgba(201,151,58,0.04)', borderRadius:6, border:'1px solid var(--blue-soft)' }}>
                                      <strong style={{color:'var(--blue)'}}>→ Recommendation:</strong> {val.recommendation}
                                    </div>
                                  )}
                                </div>
                                {score && (
                                  <div style={{ flexShrink:0, textAlign:'center', minWidth:64 }}>
                                    <div style={{ fontFamily:'var(--ff-m)', fontSize:28, fontWeight:500, color:ScoreColor(score), lineHeight:1 }}>
                                      {score.toFixed(1)}
                                    </div>
                                    <div style={{ fontSize:10, color:'var(--text3)', marginTop:2 }}>/ 5</div>
                                    {/* Score bar */}
                                    <div style={{ width:48, height:4, background:'var(--bg4)', borderRadius:2, overflow:'hidden', margin:'6px auto 0' }}>
                                      <div style={{ height:'100%', width:`${(score/5)*100}%`, background:ScoreColor(score), borderRadius:2 }} />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Top issues */}
                {findings.top_issues?.length > 0 && (
                  <div style={{ marginBottom:16 }}>
                    <div className="sec-header"><div className="sec-title">Top Issues Found</div><div className="sec-line"></div></div>
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {findings.top_issues.map((issue,i)=>(
                        <div key={i} className="insight insight-warning">
                          <div className="insight-icon">⚠️</div>
                          <div className="insight-body">{issue}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick wins + Long-term fixes side by side */}
                {(findings.quick_wins?.length > 0 || findings.long_term_fixes?.length > 0) && (
                  <div className="g2" style={{ marginBottom:16 }}>
                    {findings.quick_wins?.length > 0 && (
                      <div className="card" style={{ borderTop:'3px solid var(--green)' }}>
                        <div style={{ fontFamily:'var(--ff-d)', fontSize:15, fontWeight:800, color:'var(--green)', marginBottom:14 }}>⚡ Quick Wins</div>
                        {findings.quick_wins.map((w,i)=>(
                          <div key={i} style={{ display:'flex', gap:8, padding:'7px 0', borderBottom: i<findings.quick_wins.length-1?'1px solid var(--border)':'none' }}>
                            <span style={{color:'var(--green)',flexShrink:0,marginTop:1}}>✓</span>
                            <span style={{fontSize:13,color:'var(--text2)',lineHeight:1.5}}>{w}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {findings.long_term_fixes?.length > 0 && (
                      <div className="card" style={{ borderTop:'3px solid var(--accent)' }}>
                        <div style={{ fontFamily:'var(--ff-d)', fontSize:15, fontWeight:800, color:'var(--blue)', marginBottom:14 }}>🎯 Long-Term Fixes</div>
                        {findings.long_term_fixes.map((f,i)=>(
                          <div key={i} style={{ display:'flex', gap:8, padding:'7px 0', borderBottom: i<findings.long_term_fixes.length-1?'1px solid var(--border)':'none' }}>
                            <span style={{color:'var(--blue)',flexShrink:0,marginTop:1}}>→</span>
                            <span style={{fontSize:13,color:'var(--text2)',lineHeight:1.5}}>{f}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="card" style={{ borderColor:'rgba(201,151,58,0.15)', background:'rgba(201,151,58,0.02)', display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:14, marginBottom:3 }}>Cross-reference with live assessment data</div>
                    <div style={{ fontSize:12, color:'var(--text2)' }}>Run a fresh Org DNA assessment alongside this imported report for a combined diagnostic view.</div>
                  </div>
                  <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                    <button className="btn btn-primary btn-sm" onClick={()=>navigate('/assessment', { state: {companyId:selCompany} })}>
                      Start Assessment →
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>navigate('/ai',{ state: {companyId:selCompany,_ts:Date.now(),autoPrompt:`I've uploaded an existing audit report called "${selected.original_name}". Based on the findings: ${findings?.summary||''} Help me prioritise what to fix first and build an action plan.`} })}>
                      Discuss with AI →
                    </button>
                  </div>
                </div>
                </>)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
